import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Youtube, Eye, TrendingUp, Palette, MousePointerClick, Zap } from 'lucide-react';

const AIThumbnailGeneratorYouTube = () => {
  const faqs = [
    {
      question: "What's the best size for YouTube thumbnails?",
      answer: "YouTube recommends 1280x720 pixels with a 16:9 aspect ratio. Our AI generates thumbnails in this exact format, optimized for both desktop and mobile viewing."
    },
    {
      question: "How can AI thumbnails improve my click-through rate?",
      answer: "AI helps you create multiple thumbnail variations quickly, allowing you to A/B test different styles. Our models are trained on high-performing thumbnails to maximize CTR."
    },
    {
      question: "Can I create thumbnails matching my channel's brand?",
      answer: "Yes! Use consistent prompts and style parameters to generate thumbnails that match your channel's visual identity across all videos."
    },
    {
      question: "How many thumbnail options should I test?",
      answer: "YouTube recommends testing 3-5 variants. Our bulk generation lets you create multiple options in seconds, so you can find the highest-performing design."
    }
  ];

  const features = [
    {
      icon: <Youtube className="w-6 h-6" />,
      title: "1280x720 YouTube Format",
      description: "Perfect dimensions for YouTube's thumbnail requirements every time"
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Attention-Grabbing Designs",
      description: "AI trained on viral thumbnails to maximize viewer attention"
    },
    {
      icon: <MousePointerClick className="w-6 h-6" />,
      title: "CTR-Optimized",
      description: "Generate thumbnails designed to boost click-through rates"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Brand Consistency",
      description: "Create matching thumbnails across your entire video library"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "A/B Testing Ready",
      description: "Generate multiple variants to test which performs best"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Bulk Generation",
      description: "Create thumbnails for your entire content calendar in minutes"
    }
  ];

  const niches = [
    "Gaming and streaming content",
    "Tutorial and educational videos",
    "Vlog and lifestyle content",
    "Product reviews and unboxings",
    "Tech and gadget channels",
    "Cooking and recipe videos",
    "Fitness and workout content",
    "Business and finance education"
  ];

  return (
    <>
      <SEO
        title="AI YouTube Thumbnail Generator | Create Click-Worthy Thumbnails"
        description="Generate high-CTR YouTube thumbnails with AI. Create 1280x720 thumbnails that get clicks. Bulk generate, A/B test, and boost your video views instantly."
        keywords="youtube thumbnail generator ai, ai thumbnail maker, youtube thumbnail creator, video thumbnail ai, youtube thumbnail size, bulk thumbnail generator, youtube ctr optimization"
        url="/ai-thumbnail-generator-youtube"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI YouTube Thumbnail Generator', url: '/ai-thumbnail-generator-youtube' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-red-600/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-red-500/10 text-red-500 border-red-500/20">
                YouTube-Optimized AI Thumbnails
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI YouTube Thumbnail Generator
                <span className="block text-red-500">Boost Your Click-Through Rate</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Generate scroll-stopping YouTube thumbnails with AI. Perfect 1280x720 format, 
                A/B test variants, and maximize views for every video.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-red-500 hover:bg-red-600">
                  <Link to="/try-generator">
                    Create Thumbnails Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Thumbnail Examples</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 border-y border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-3xl font-bold text-red-500">90%</p>
                <p className="text-muted-foreground">of top videos have custom thumbnails</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-red-500">2x</p>
                <p className="text-muted-foreground">higher CTR with optimized thumbnails</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-red-500">3 sec</p>
                <p className="text-muted-foreground">to generate each thumbnail</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-red-500">1M+</p>
                <p className="text-muted-foreground">thumbnails generated</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Everything You Need for YouTube Success
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border-red-500/10 hover:border-red-500/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center text-red-500 mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Niches */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Every YouTube Niche
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {niches.map((niche, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                    <CheckCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span>{niche}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Getting More Views Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 20,000+ YouTubers using AI to create better thumbnails. 
                More clicks. More views. More subscribers.
              </p>
              <Button asChild size="lg" className="bg-red-500 hover:bg-red-600">
                <Link to="/try-generator">
                  Generate Thumbnails Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIThumbnailGeneratorYouTube;
